import { css, cx } from "@emotion/css";
import { CircularProgress } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon, CheckCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDGatewayHealthStatus } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import * as React from "react";
type GatewayDiscoveryProgressProps = {
    name: string;
    isFound: boolean;
    bearerTokenExpired?: boolean;
    gatewayHealthStatus: ArgoCDGatewayHealthStatus;
};
export const GatewayDiscoveryProgress = (props: GatewayDiscoveryProgressProps) => {
    let boxStyle: string | undefined = undefined;
    if (props.isFound) {
        if (props.bearerTokenExpired || props.gatewayHealthStatus === "Unavailable") {
            boxStyle = styles.failure;
        }
        else if (props.gatewayHealthStatus === "ArgoCDConnectionIssues") {
            boxStyle = styles.warning;
        }
        else if (props.gatewayHealthStatus === "Healthy") {
            boxStyle = styles.success;
        }
    }
    const registrationContent = useMemo(() => {
        if (props.isFound) {
            return (<>
                    <div className={styles.iconContainer}>
                        <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>
                    </div>
                    <span>
                        <b>{props.name}</b> established a connection
                    </span>
                </>);
        }
        else if (props.bearerTokenExpired) {
            return (<>
                    <div className={styles.iconContainer}>
                        <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>
                    </div>
                    <span>
                        <strong>Your bearer token expired</strong> before the agent could establish a connection. To try again, add another Argo CD Gateway.
                    </span>
                </>);
        }
        else {
            return (<>
                    <div className={styles.iconContainer}>
                        <CircularProgress size="container" aria-hidden="true"/>
                    </div>
                    <span>
                        Waiting for <b>{props.name}</b> to establish a connection...
                    </span>
                </>);
        }
    }, [props.bearerTokenExpired, props.isFound, props.name]);
    const healthCheckContent = useMemo(() => {
        switch (props.gatewayHealthStatus) {
            case "Unknown":
                return props.isFound ? (<>
                        <div className={styles.iconContainer}>
                            <CircularProgress size="container" aria-hidden="true"/>
                        </div>
                        <span>
                            <span>Performing health check...</span>
                        </span>
                    </>) : null;
            case "Healthy":
                return (<>
                        <div className={styles.iconContainer}>
                            <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>
                        </div>
                        <span>
                            <b>{props.name}</b> is healthy
                        </span>
                    </>);
            case "ArgoCDConnectionIssues":
                return (<>
                        <div className={styles.iconContainer}>
                            <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>
                        </div>
                        <span>
                            <b>{props.name}</b> has issues connecting to the target Argo CD instance
                        </span>
                    </>);
            case "Unavailable":
                return (<>
                        <div className={styles.iconContainer}>
                            <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>
                        </div>
                        <span>
                            <b>{props.name}</b> health check failed
                        </span>
                    </>);
        }
    }, [props.gatewayHealthStatus, props.isFound, props.name]);
    return (<div className={cx(styles.box, boxStyle)} role="alert">
            <div className={styles.contentContainer}>{registrationContent}</div>
            {healthCheckContent && <div className={styles.contentContainer}>{healthCheckContent}</div>}
        </div>);
};
const styles = {
    box: css({
        padding: `${space[12]} ${space[16]}`,
        alignSelf: "stretch",
        display: "grid",
        marginTop: space[16],
        fontSize: "14/16rem",
        color: themeTokens.color.text.primary,
        borderRadius: borderRadius.small,
        backgroundColor: themeTokens.color.callout.background.info.default,
    }),
    success: css({
        backgroundColor: themeTokens.color.callout.background.success.default,
    }),
    warning: css({
        backgroundColor: themeTokens.color.callout.background.attention.default,
    }),
    failure: css({
        backgroundColor: themeTokens.color.callout.background.danger.default,
    }),
    contentContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        "&:not(:last-child)": {
            marginBottom: space[16],
        },
    }),
    iconContainer: css({
        padding: `${space[4]} ${space[2]} 0 ${space[2]}`,
        marginRight: space[4],
        width: "24px",
    }),
};
